/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a patient drug.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialPatientsDrugsState {
    patientsDrugs: Dtos.PatientDrug[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IPatientsDrugsState {
    patientsDrugs: IInitialPatientsDrugsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const PatientsDrugsInitialState: IInitialPatientsDrugsState = {
    patientsDrugs: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const usePatientsDrugsSelector: TypedUseSelectorHook<IPatientsDrugsState> = useSelector;

export const PatientsDrugsSelectors = {
    selectPatientsDrugs: (state: IPatientsDrugsState) => state.patientsDrugs.patientsDrugs,
    selectLoadState: (state: IPatientsDrugsState) => state.patientsDrugs.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class PatientsDrugsReducer extends ImmerReducer<IInitialPatientsDrugsState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(patientsDrugs?: Dtos.PatientDrug[]) {
        this.draftState.patientsDrugs = patientsDrugs ? patientsDrugs : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.patientsDrugs = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...PatientsDrugsInitialState };
    }
};

export const PatientsDrugsActions = createActionCreators(PatientsDrugsReducer);
const reducer = createReducerFunction(PatientsDrugsReducer, PatientsDrugsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const PatientsDrugsApi = {
    load: () => {
        return client.get(new Dtos.GetPatientDrugs());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const PatientsDrugsLogic = {
    load: createLogic<IPatientsDrugsState, {}, undefined, string, ReturnType<typeof PatientsDrugsActions.load>>({
        type: PatientsDrugsActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await PatientsDrugsApi.load();

                dispatch(PatientsDrugsActions.loadSuccess(
                    response.patientDrugs
                ));
            }
            catch (error: any) {
                dispatch(PatientsDrugsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    PatientsDrugsLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const usePatientsDrugs = (): [Dtos.PatientDrug[] | null, IRequestState<Dtos.ResponseStatus>, typeof PatientsDrugsActions] => {
    const patientsDrugs = useSelector(PatientsDrugsSelectors.selectPatientsDrugs);
    const loadState = useSelector(PatientsDrugsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PatientsDrugsActions>(() => {
        return bindActionCreators(PatientsDrugsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PatientsDrugsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        patientsDrugs,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('patientsDrugs', reducer as any, logics as any);